import { useEffect, useCallback } from 'react';
import { useMapStore, type StateSummary } from '../store/useMapStore';
import type { ElectionResult } from '../types/gis';
import { getPartyColor, getPartyAbbreviation } from '../utils/partyLogo';
import { SHOW_ELECTION_RESULTS } from '../config/features';

const STATIC_RESULTS_URL = '/data/election/tamil_nadu_2026_results.json';

export const useElectionResults = () => {
  const { setElectionResults, setIsElectionLive, activeLayer } = useMapStore();

  const fetchResults = useCallback(async () => {
    try {
      const response = await fetch(STATIC_RESULTS_URL);
      if (!response.ok) throw new Error('Failed to load static election dataset');
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allData: any[] = await response.json();
      
      const results: Record<number, ElectionResult> = {};
      const partyTallies: Record<string, number> = {};

      allData.forEach((constData) => {
        const num = constData.constituency_number;
        if (!num) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const winner = constData.candidates.find((c: any) => c.status.toLowerCase() === 'won');
        if (!winner) return;

        const partyAbbr = getPartyAbbreviation(winner.party);
        const color = getPartyColor(winner.party);

        results[num] = {
          party: winner.party,
          candidate: winner.candidate_name,
          color: color,
          status: 'WON',
          constituencyId: num
        };

        partyTallies[partyAbbr] = (partyTallies[partyAbbr] || 0) + 1;
      });

      // Convert tallies to StateSummary array sorted by count
      const summary: StateSummary[] = Object.entries(partyTallies)
        .map(([party, count]) => ({
          party: party,
          won: count,
          leading: 0,
          total: count
        }))
        .sort((a, b) => b.total - a.total);

      // Push everything to store once
      setElectionResults(results, undefined, summary);
      setIsElectionLive(true); // Flag that static finalized data is loaded
    } catch (err) {
      console.error('Election static load error:', err);
    }
  }, [setElectionResults, setIsElectionLive]);

  useEffect(() => {
    if (SHOW_ELECTION_RESULTS && activeLayer === 'CONSTITUENCY') {
      const timer = setTimeout(() => {
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(() => fetchResults(), { timeout: 2000 });
        } else {
          fetchResults();
        }
      }, 500); // Wait 500ms before starting the fetch to let the map init
      return () => clearTimeout(timer);
    }
  }, [activeLayer, fetchResults]);

  return { refresh: fetchResults };
};
